import React, { useState, useEffect, useCallback } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid"; // Import search icon
import axiosInstance from "../../utils/axiosInstance";
import HospitalTable from "./HospitalTable";
import Pagination from "../Doctors/Pagination";
import HospitalModel from "./HospitalModel"; // Import modal component
import SuccessMessage from "../../Components/shared/SuccessMessage"; // ✅ Import SuccessMessage
import PageHeader from "../../Components/PageHeader";

const Hospital = () => {
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospitals, setFilteredHospitals] = useState([]); // State for filtered hospitals
  const [searchQuery, setSearchQuery] = useState(""); // Search input state
  const [currentPage, setCurrentPage] = useState(1);
  const hospitalsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  // State for the modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null); // Stores hospital data for editing

  // ✅ Success Message State
  const [successMessage, setSuccessMessage] = useState({
    show: false,
    message: "",
    isError: false,
  });

  // Fetch hospitals from backend
  const fetchHospitals = useCallback(async () => {
    try {
      let response = await axiosInstance.get(
        `/hospitals?page=${currentPage}&limit=${hospitalsPerPage}&search=${searchQuery}`
      );

      const fetchedHospitals = response.data.hospitals;
      setHospitals(fetchedHospitals);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    }
  }, [currentPage, hospitalsPerPage, searchQuery]); // ✅ Now searchQuery is sent to backend
  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  useEffect(() => {
    setFilteredHospitals(
      searchQuery.trim() === ""
        ? hospitals
        : hospitals.filter((hospital) =>
            hospital.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
    );
  }, [searchQuery, hospitals]);

  // Open modal for editing a hospital
  const handleEditHospital = (hospital) => {
    setSelectedHospital(hospital);
    setIsModalOpen(true);
  };

  // ✅ Function to handle success message
  const showSuccessMessage = (message, isError = false) => {
    setSuccessMessage({ show: true, message, isError });

    // Auto-hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage({ show: false, message: "", isError: false });
    }, 3000);
  };

  return (
    <div className="p-6">
      {/* Page Header */}
      <PageHeader
        title="Hospital/Branch Information"
        subtitle="View your hospital branch details"
        showBackButton={true}
      />

      {/* ✅ Display Success Message */}
      {successMessage.show && (
        <SuccessMessage
          message={successMessage.message}
          isError={successMessage.isError}
        />
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative w-full sm:w-96">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search hospitals by name..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Hospital Table */}
      <div className="overflow-x-auto">
        <HospitalTable
          hospitals={filteredHospitals}
          onEdit={handleEditHospital}
        />
      </div>

      {/* Pagination Component */}
      <div className="mt-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onNextPage={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Hospital Modal */}
      <HospitalModel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        hospital={selectedHospital}
        refreshHospitals={fetchHospitals}
        showSuccessMessage={showSuccessMessage} // ✅ Pass success message function to modal
      />
    </div>
  );
};

export default Hospital;
